/**
 * Pooled audio-label client (superuser-only).
 *
 * BE: services/plugins/ml_models_lifecycle/api/audio_pool_views.py, mounted at
 * /ml_models_lifecycle/audio/pool/. Same shape as the table and shelf pool clients, with
 * the one thing speech changes:
 *
 * **`corrected_text` has THREE states and they are all meaningful.**
 *   `null` — nobody has listened yet;
 *   `""`   — a human heard noise or something unintelligible and said so. This is a REAL
 *            training target: it teaches the model to emit silence. 88 of the 175 labels
 *            that exist today are blanks.
 *   text   — the verbatim transcript.
 * Never `?? ''` a value out of this module, and never send `""` when you mean "leave it
 * alone" — omit the field instead.
 */
import { axiosInstance } from './api';
import type { AudioUtterance } from './datasets';

export type Labelled = 'all' | 'human' | 'ai' | 'unlabelled';

/** Where the label row came from. `imported` = the frozen audio-transcripts dataset. */
export type Origin = 'collected' | 'imported';

/** Ordering. The default (recording date, newest first) is deliberate: recent audio
 *  matches the microphones, the room and the menu in use now. */
export type Sort = '' | 'oldest' | 'annotated_desc' | 'annotated_asc';

export type View =
  | 'imported' | 'collected' | 'unlabelled'
  | 'human' | 'ai' | 'all';

export const VIEW_ROWS: View[][] = [
  ['imported', 'collected', 'unlabelled'],
  ['human', 'ai', 'all'],
];

export const viewParams = (v: View): { labelled: Labelled; origin: Origin | '' } => {
  if (v === 'imported') return { labelled: 'unlabelled', origin: 'imported' };
  if (v === 'collected') return { labelled: 'unlabelled', origin: 'collected' };
  return { labelled: v as Labelled, origin: '' };
};

export interface AudioPoolZone {
  zone_id: number;
  zone: string;
  total: number;
  todo: number;
}

export interface AudioPoolItem {
  id: number;
  audio_record_id: number;
  zone_id: number;
  conversation_id: number | null;
  recorded_at: string | null;
  audio_url: string;
  /** What the editor pre-fills with — the large-model draft where one exists, else the
   *  production transcript. */
  draft_text: string;
  /** Always the production model's output, shown as reference (the old tool called this
   *  «малая модель»). */
  prod_text: string;
  /** Per-utterance timings for the waveform. Absent on pre-2026-06 clips. */
  segments: AudioUtterance[];
  duration: number;
  clip_status: string | null;
  /** null = not judged. "" = noise. Do not collapse them. */
  corrected_text: string | null;
  labelled: boolean;
  origin: Origin;
  source: 'user' | 'ai' | null;
  annotated_by: string | null;
  annotated_at: string | null;
}

export interface AudioPoolSamples {
  total: number;
  page: number;
  page_size: number;
  labelled: Labelled;
  origin: Origin | '';
  zone_total: number | null;
  facets: {
    labelled: Record<Labelled, number>;
    unlabelled_origins: Record<Origin, number>;
  };
  items: AudioPoolItem[];
}

export const getAudioPoolZones = () =>
  axiosInstance.get<{ zones: AudioPoolZone[] }>('/ml_models_lifecycle/audio/pool/zones/');

export const getAudioPoolSamples = (params: {
  zone_id: number | null; labelled: Labelled;
  origin?: Origin | ''; sort?: Sort; batch?: number;
  page?: number; page_size?: number;
}) =>
  axiosInstance.get<AudioPoolSamples>('/ml_models_lifecycle/audio/pool/samples/', {
    params: {
      ...(params.batch ? { batch: params.batch } : { zone_id: params.zone_id }),
      labelled: params.labelled,
      ...(params.origin && !params.batch ? { origin: params.origin } : {}),
      ...(params.sort ? { sort: params.sort } : {}),
      page: params.page ?? 1, page_size: params.page_size ?? 50,
    },
  });

/** `corrected_text: null` retracts a verdict (back to the queue, provenance cleared).
 *  `""` marks the clip noise — a deliberate blank target, not a retraction. */
export type AudioLabelDelta = { id: number; corrected_text: string | null };

export const audioPoolAnnotate = (items: AudioLabelDelta[]) =>
  axiosInstance.post<{ saved: number }>('/ml_models_lifecycle/audio/pool/annotate/', { items });

export const audioPoolDelete = (ids: number[], deleted = true) =>
  axiosInstance.post<{ updated: number; deleted: boolean }>(
    '/ml_models_lifecycle/audio/pool/delete/', { ids, deleted });

export interface AudioTrainStatus {
  labelled_now: number;
  /** Clips labelled as noise. Broken out because a set that is mostly blanks trains a
   *  model that says nothing. */
  blanks: number;
  speech: number;
  unlabelled: number;
  held_out: number;
  new_since_last: number | null;
  last_run: {
    created_at: string;
    trained_up_to: number;
    snapshot_items: number | null;
    snapshot_version_id: string;
    model_version: string;
    dagster_run_url: string;
  } | null;
}

export const getAudioTrainStatus = () =>
  axiosInstance.get<AudioTrainStatus>('/ml_models_lifecycle/audio/pool/train-status/');

export interface DagsterRunRef {
  run_id: string; status: string; url: string;
  /** true = a run of this job was already going; the BE returned it instead of starting another. */
  already_running?: boolean;
}

/** Fine-tune on what is labelled right now. A human decides the moment, not a counter:
 *  labelling here is 30 seconds of listening per clip, so the pool grows in bursts that
 *  say nothing about readiness. */
export const audioPoolRetrain = () =>
  // Dagster's launchRun answers in ~25-60 s — past the global 30 s timeout, which
  // reported a run that was in fact training as "failed" (28.10.2026).
  axiosInstance.post<DagsterRunRef>('/ml_models_lifecycle/audio/pool/retrain/', undefined, { timeout: 120000 });
